import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, ExternalLink, Clock } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
} from '@/components/ui';
import type { Transaction } from '@/types';

interface TransactionStatusProps {
  /** The created transaction */
  transaction: Transaction;
  /** Called when user wants to create another transaction */
  onReset?: () => void;
}

/**
 * Displays the result of a successfully created transaction.
 *
 * Shows:
 * - Transaction ID
 * - Redirect URL with copy button
 * - Flow type and status
 * - Expiration time
 * - Button to open redirect URL in new tab
 */
export function TransactionStatus({ transaction, onReset }: TransactionStatusProps) {
  const { t } = useTranslation('transactions');
  const [copied, setCopied] = React.useState(false);

  const handleCopyUrl = async () => {
    if (!transaction.RedirectUrl) return;
    try {
      await navigator.clipboard.writeText(transaction.RedirectUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to copy URL:', err);
    }
  };

  const handleOpenUrl = () => {
    if (!transaction.RedirectUrl) return;
    window.open(transaction.RedirectUrl, '_blank', 'noopener,noreferrer');
  };

  const parsedExpiry = transaction.ExpiresAt ? new Date(transaction.ExpiresAt) : null;
  const expiresAt = parsedExpiry && !isNaN(parsedExpiry.getTime()) ? parsedExpiry : null;
  const now = new Date();
  const minutesRemaining = expiresAt
    ? Math.max(0, Math.round((expiresAt.getTime() - now.getTime()) / 60000))
    : null;

  return (
    <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-green-100 p-1 dark:bg-green-900">
            <Check className="h-5 w-5 text-success" aria-hidden="true" />
          </div>
          <CardTitle className="text-success">
            {t('status.title')}
          </CardTitle>
        </div>
        <CardDescription>
          {t('status.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Transaction Details */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{t('status.transactionId')}</p>
            <p className="font-mono text-sm">{transaction.TransactionID}</p>
          </div>
          {transaction.FlowType && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('status.flowType')}</p>
              <Badge variant="outline">{transaction.FlowType}</Badge>
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-muted-foreground">{t('status.status')}</p>
            <Badge variant="secondary">{transaction.Status}</Badge>
          </div>
          {expiresAt && minutesRemaining != null && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('status.validUntil')}</p>
              <div className="flex items-center gap-1 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span>
                  {expiresAt.toLocaleTimeString('de-DE')} ({t('status.minutesRemaining', { minutes: minutesRemaining })})
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Redirect URL — may be absent for WEB_TAN flows (Merchant API v2.0) */}
        {transaction.RedirectUrl ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{t('status.redirectUrl')}</p>
            <div className="flex gap-2">
              <code className="flex-1 overflow-x-auto rounded-md bg-muted p-3 text-sm">
                {transaction.RedirectUrl}
              </code>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyUrl}
                aria-label={copied ? t('status.copied') : t('status.copyUrl')}
              >
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('status.copied')}
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('status.copyUrl')}
                  </>
                )}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleOpenUrl}
              >
                <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                {t('status.openUrl')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{t('status.redirectUrl')}</p>
            <p className="text-sm text-muted-foreground italic">
              {t('status.redirectUrlUnavailable')}
            </p>
          </div>
        )}

        {/* Salt Info - only shown when Facade provides it */}
        {transaction.Salt && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{t('status.salt')}</p>
            <code className="block overflow-x-auto rounded-md bg-muted p-3 text-sm">
              {transaction.Salt}
            </code>
          </div>
        )}

        {/* Actions */}
        {onReset && (
          <div className="pt-4 border-t">
            <Button variant="outline" onClick={onReset}>
              {t('status.createNew')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
